import { useEffect, useRef, useCallback, RefObject } from "react";

export const useClickOutside = <T extends HTMLElement>(props: {
  handler?: () => void;
  elRef?: RefObject<T> | T | null;
  ignoreRefs?: HTMLElement[] | [];
}) => {
  const { handler, elRef, ignoreRefs } = props;
  const createdRef = useRef<T>(null);
  const ref = useRef<T | null>(null);

  if (elRef instanceof HTMLElement) {
    ref.current = elRef;
  } else if (elRef && "current" in elRef) {
    ref.current = elRef.current;
  } else {
    ref.current = createdRef.current;
  }

  const handlerRef = useRef<VoidFunction | null>(null);
  useEffect(() => {
    if (handler) {
      handlerRef.current = handler;
    }
  }, [handler]);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        handlerRef.current &&
        ref.current &&
        !ref.current.contains(event.target as Node) &&
        // If clicked item is inside ignoreRefs list - do not call callback
        (!ignoreRefs ||
          ignoreRefs?.every((item) => !item?.contains(event.target as Node)))
      ) {
        handlerRef.current();
      }
    },
    [ignoreRefs]
  );

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);

    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [handleClickOutside]);

  return ref;
};
